import {
    Component,
    Element,
    Event,
    EventEmitter,
    h,
    Host,
    Listen,
    Prop,
    Watch,
} from "@stencil/core";
import { ScaleBand, scaleLinear, ScaleLinear, ScaleTime } from "d3-scale";
import { BaseType, Selection } from "d3-selection";
import { debounce } from "../../../utils/debounce";
import { transform } from "../../../utils/transform";
import { getUniqueId } from "../../../utils/unique-id";
import { ChartData, ChartGroupDataValues } from "../interfaces/chart-data";
import {
    ChartDataGroupConfig,
    LineChartDataGroupConfigItem,
} from "../interfaces/chart-data-group-config";
import { ChartMargin } from "../interfaces/chart-margin";
import { ChartState } from "../interfaces/chart-state";
import { LineChartBandScaleSpecifics } from "./line-chart-band-scale-specifics";
import { LineChartScaleSpecifics } from "./line-chart-scale-specifics";
import { LineChartSvg } from "./line-chart-svg";
import { LineChartTimeScaleSpecifics } from "./line-chart-time-scale-specifics";
import { getCssClassForKey, getLabelForKey } from "../utils/group-config-utils";

export type LineChartData = {
    name: string;
    values: LineChartDataItem[];
    cssClass: string;
    ignore: boolean;
};
export type LineChartDataItem = {
    key: string | Date;
    value: number;
};
export type LineDataItem = number[] & { data: ChartGroupDataValues };
export type LineChartGroupItem = LineDataItem[] & {
    index: number;
    key: string;
};
export type LineChartXScaleType = "band" | "time";
export type LineChartCurveFunctionName =
    | "curveLinear"
    | "curveStep"
    | "curveStepBefore"
    | "curveStepAfter"
    | "curveBasis"
    | "curveBundle"
    | "curveCardinal"
    | "curveMonotoneX"
    | "curveNatural"
    | "curveCatmullRom";
export type LineChartXDomain = string | Date;
export type LineChartTooltipFromatterInputData = {
    key: LineChartXDomain;
    values: { [groupKey: string]: number };
};

@Component({
    tag: "uic-line-chart",
    styleUrl: "line-chart.scss",
})
export class LineChart {
    /** The data prop for binding. Object where each key is a datapoint on the x axis. The value is an object as well: Each key corresponds to a data line/group */
    @Prop() data: ChartData;

    /** The groupConfig property for binding the chart, it contains a CSS class and label field.
     * The CSS class is used for coloring the chart.
     */
    @Prop() groupConfig: ChartDataGroupConfig<
        ChartGroupDataValues,
        LineChartDataGroupConfigItem
    >;

    /** locale for overriding the browser-default. This is needed for a reliable testing setup */
    @Prop() locale: string = "";

    /** Select the x axis scale to use
     * Time scales always keep a continuous timeline even if time values are skipped in the input data.
     * Band scales allow for skipped values even if using a time measure for the x axis.
     * You need to supply the `data` group keys as strings. It's best to use the standard iso format of yyyy-MM-dd'T'HH-mm-ss'Z' when using the time scale.
     */
    @Prop() xScaleType: LineChartXScaleType = "time";

    /** Choose one of d3's curve interpolation functions.
     * The default ist curveLinear (= no interpolation)
     * see e.g. http://bl.ocks.org/d3indepth/b6d4845973089bc1012dec1674d3aff8 for a quick comparison
     */
    @Prop() curveFunctionName: LineChartCurveFunctionName = "curveLinear";

    /** Whether the datapoint dots shouldn't be displayed */
    @Prop() hideDots: boolean = false;

    /** Number of ticks (entries) on the Y axis.
     * In some cases d3 overwrites this value and chooses the best automatically,
     * depending on the current width and height of the chart.
     */
    @Prop() yTicks: number = 6;

    /** The maximum value to display on the y axis
     * Useful for showing values in context of an expected range (e.g. 0-100%)
     * If not set (default) the maximum y value is used.
     */
    @Prop() yMaxValue: number;

    /** The minimum value to display on the y axis
     * Useful for showing values in context of an expected range (e.g. 0-100%)
     * If not set (default) the minimum y value is used.
     */
    @Prop() yMinValue: number;

    /** Label for the Y axis. */
    @Prop() yAxisLabel: string;

    /** X axis formatter, set a custom format function for x axis ticks.
     * As keys might be Date (when using a time scale) both datatypes have to be supported
     */
    @Prop() xFormatter: (x: LineChartXDomain) => string;

    /** Y axis formatter, set a custom format function for y axis ticks. As D3 automatically chooses human-friendly ticks it might be more useful to change the keys in your data object. */
    @Prop() yFormatter: (x: number) => string;

    /** Use this flag if you don't want to use the tooltips */
    @Prop() disableTooltip: boolean = false;

    /** Tooltip text formatter. Gets the x axis key and values for all data points at that location as input.
     * There's a default formatter which sorts by value, uses x and y formatters and adds a color dot per group by setting the group css class
     */
    @Prop() tooltipFormatter: (
        x: LineChartTooltipFromatterInputData
    ) => string = this.defaultTooltipFormatter.bind(this);

    /** Customize the margin. You don't have to change them, labels won't be truncated regardless of these values */
    @Prop() margin: ChartMargin = { top: 20, right: 20, bottom: 20, left: 20 };

    /** Can be used to define a custom line chart root class for styling purposes.*/
    @Prop() lineChartRootClass: string = "";

    /** State in which the line chart is currently in. */
    @Prop() state: ChartState = ChartState.SUCCESS;

    /** Text for displaying when no data is available. */
    @Prop() noDataText: string = "No data available";

    /** Set the transition duration. As long as the transitions do not cause any issues, keep the default */
    @Prop() transitionDurationMs: number = 500;

    /** id attribute for this chart. A unique id will be generated if this is empty */
    @Prop({ mutable: true }) chartId?: string = "";

    /** Set this prop to one of your group keys (= lines) to highlight it */
    @Prop() selectedGroup: string = undefined;

    /** Emitted when a data line or dot is click/touched
     * The default action of highlighting the selected line may be prevented with `e.preventDefault()`
     */
    @Event({ eventName: "uicLineSelect" })
    lineSelect: EventEmitter<LineChartData>;

    /**
     * @internal
     */
    @Event({ eventName: "uicLineSelectInternal" })
    private lineSelectInternal: EventEmitter<LineChartData>;

    @Element() el: HTMLUicLineChartElement;
    private tooltip: HTMLUicTooltipElement;
    private scaleSpecifics: LineChartScaleSpecifics<
        ScaleBand<string> | ScaleTime<number, number>
    >;

    private readonly debouncedUpdateSvg = debounce(() => this.updateSvg(), 100);

    @Listen("resize", { target: "window" })
    onResize() {
        this.debouncedUpdateSvg();
    }

    @Listen("uicLineSelectInternal")
    onLineSelect(e) {
        const lineSelectEvent = this.lineSelect.emit(e.detail);
        if (lineSelectEvent.defaultPrevented) {
            return;
        }

        if (!e.detail.name || this.selectedGroup === e.detail.name) {
            this.selectedGroup = undefined;
        } else {
            this.selectedGroup = e.detail.name;
        }
    }

    @Listen("click", { passive: true })
    onClick() {
        this.selectedGroup = undefined;
    }

    private svg: LineChartSvg;
    private yAxisLabelOffset: number = 30;
    private xAxisLabelRotationDegrees = 65;

    constructor() {
        if (this.xScaleType === "time") {
            // Set reasonable default formatter for dates
            this.xFormatter = this.defaultXDateFormatter.bind(this);
            this.scaleSpecifics = new LineChartTimeScaleSpecifics();
        } else {
            this.scaleSpecifics = new LineChartBandScaleSpecifics();
        }
    }

    componentDidLoad() {
        this.initSvg();
    }

    @Watch("chartId")
    componentWillLoad() {
        const { chartId } = this;
        this.chartId = chartId || getUniqueId();
    }

    @Watch("curveFunctionName")
    curveFunctionChanged() {
        this.updateChart();
    }

    @Watch("xScaleType")
    xScaleTypeChanged(val: LineChartXScaleType) {
        if (val === "time") {
            this.scaleSpecifics = new LineChartTimeScaleSpecifics();
        } else {
            this.scaleSpecifics = new LineChartBandScaleSpecifics();
        }
    }

    componentDidRender() {
        this.updateSvg();
    }

    private initSvg() {
        const {
            width: parentWidth,
            height: parentHeight,
        } = this.el.parentElement.getBoundingClientRect();

        this.svg = new LineChartSvg(this.chartId, parentWidth, parentHeight);

        const chartArea = this.svg.appendChartArea(this.margin);
        chartArea.appendYAxis();
        chartArea.appendXAxis(parentHeight, this.margin);
        chartArea.appendDataGroupArea();

        this.svg.appendYAxisLabelArea();
        this.updateSvg();
    }

    private updateSvg() {
        if (this.svg === undefined) return;

        if (
            this.state !== ChartState.NODATA &&
            this.data !== undefined &&
            this.data !== null &&
            Object.keys(this.data).length > 0
        ) {
            this.svg.show();
            this.updateChart();
        } else {
            this.svg.hide();
        }
    }

    private updateChart() {
        const {
            width: parentWidth,
            height: parentHeight,
        } = this.el.parentElement.getBoundingClientRect();
        const yAxisLabelOffset = LineChart.hasYAxisLabel(this.yAxisLabel)
            ? this.yAxisLabelOffset
            : 0;
        const yAxisLabelData = LineChart.hasYAxisLabel(this.yAxisLabel)
            ? [this.yAxisLabel]
            : [];
        const [minYValue, maxYValue] = LineChart.yValueLimits(
            this.data,
            this.yMinValue,
            this.yMaxValue
        );
        const maxYTickLabelWidth = LineChart.maxYTickLabelWidth(
            this.svg.chartArea.getYAxisArea(),
            maxYValue,
            this.yFormatter
        );
        const xAxisWidth = LineChart.xScaleWidth(
            parentWidth,
            this.margin,
            maxYTickLabelWidth,
            yAxisLabelOffset
        );
        const xScale = this.scaleSpecifics.xScale(this.data, xAxisWidth);
        const xAxisEntries = this.scaleSpecifics.xAxisEntries(this.data);
        const maxXTickLabelWidth = LineChart.maxXTickLabelWidth(
            this.svg.chartArea.getXAxisArea(),
            xAxisEntries,
            this.xFormatter
        );
        const shouldRotateXAxisLabels = this.scaleSpecifics.isXLabelTooWide(
            xScale,
            maxXTickLabelWidth,
            xAxisEntries
        );
        const additionalXTickLabelSpace = shouldRotateXAxisLabels
            ? maxXTickLabelWidth *
              Math.sin(this.xAxisLabelRotationDegrees * (Math.PI / 180))
            : 0;
        const yScale = LineChart.yScale(
            parentHeight -
                this.margin.bottom -
                this.margin.top -
                additionalXTickLabelSpace,
            maxYValue,
            minYValue
        );
        const closestDataPointFn = this.scaleSpecifics.closestDataPointFnWith(
            this.data,
            xScale,
            this.margin.left + maxYTickLabelWidth + yAxisLabelOffset,
            this.selectedGroup
        );
        const xPositionForKeyFn = this.scaleSpecifics.xPositionForKey(xScale);

        this.svg
            .updateDimensions(
                parentWidth,
                parentHeight,
                this.transitionDurationMs
            )
            .updateYAxisLabel(
                yAxisLabelData,
                this.margin,
                parentHeight,
                this.transitionDurationMs
            )
            .addTooltipHandler(
                this.tooltip,
                closestDataPointFn,
                xScale,
                yScale,
                this.margin.left +
                    yAxisLabelOffset +
                    maxYTickLabelWidth +
                    this.scaleSpecifics.xOffset(xScale),
                this.margin.top,
                this.tooltipFormatter
            )
            .chartArea.updateChartArea(
                this.margin,
                yAxisLabelOffset,
                maxYTickLabelWidth
            )
            .updateYAxis(
                yScale,
                this.yTicks,
                this.yFormatter,
                xAxisWidth,
                this.transitionDurationMs
            )
            .updateXAxis(
                xScale,
                parentHeight,
                shouldRotateXAxisLabels,
                this.xAxisLabelRotationDegrees,
                additionalXTickLabelSpace,
                this.margin,
                this.xFormatter,
                this.transitionDurationMs
            )
            .updateDataGroups(
                LineChart.prepareData(
                    this.data,
                    this.groupConfig,
                    this.scaleSpecifics.mapValues,
                    this.selectedGroup
                )
            )
            .updateLines(
                xPositionForKeyFn,
                yScale,
                this.curveFunctionName,
                this.transitionDurationMs,
                this.lineSelectInternal
            )
            .updateDots(
                xPositionForKeyFn,
                yScale,
                this.transitionDurationMs,
                this.hideDots,
                this.lineSelectInternal
            );
    }

    /** Default tooltip formatter, sorts by value, uses x and y formatters and adds a color dot per group by setting the group css class */
    private defaultTooltipFormatter({
        key,
        values,
    }: LineChartTooltipFromatterInputData) {
        const formattedXKey = transform(this.xFormatter, key);
        const dataGroupsAndValues = Object.entries(values)
            .sort((a, b) => (a[1] < b[1] ? 1 : b[1] <= a[1] ? -1 : 0))
            .reduce((s, e) => {
                const cssClass = getCssClassForKey(this.groupConfig, e[0]);
                const dataGroup = getLabelForKey(this.groupConfig, e[0]);
                const formattedYValue = transform(this.yFormatter, e[1]);
                return (
                    s +
                    `<div style="white-space: nowrap;"><span class="${cssClass} uic-line-chart__tooltip-color-indicator"></span>${dataGroup}: ${formattedYValue}</div>`
                );
            }, "");

        return `<div><b>${formattedXKey}</b></div><div>${dataGroupsAndValues}</div>`;
    }

    private defaultXDateFormatter(x: string | Date) {
        if (typeof x === "string") {
            return x;
        }

        const locale =
            this.locale || Intl.DateTimeFormat().resolvedOptions().locale;
        if (x.getSeconds()) {
            return x.toLocaleTimeString(locale, {
                second: "numeric",
            });
        }

        if (x.getMinutes()) {
            return x.toLocaleTimeString(locale, {
                minute: "numeric",
                hour: "numeric",
            });
        }

        if (x.getHours()) {
            return x.toLocaleTimeString(locale, {
                hour: "numeric",
                minute: "numeric",
            });
        }

        return x.toLocaleDateString(locale, {
            month: "short",
            year: "numeric",
            day: "numeric",
        });
    }

    private static hasYAxisLabel(yAxisLabel: string) {
        return yAxisLabel && yAxisLabel.trim().length > 0 ? true : false;
    }

    private static maxXTickLabelWidth(
        xAxisArea: Selection<SVGElement, unknown, BaseType, unknown>,
        data: LineChartXDomain[],
        xFormatter: (x: LineChartXDomain) => string
    ) {
        const transformedKeys = data.map((k) => transform(xFormatter, k));
        const longestKey = transformedKeys.reduce(
            (max: string, k: string) =>
                k.toString().length > max.length ? k.toString() : max,
            ""
        );
        // account for font size specific margin (use of em to place rotatetd labels, see above)
        const stringToMeasure = `00${longestKey}00`;

        const dummyLabel = xAxisArea.append("text").text(stringToMeasure);
        const textLength = dummyLabel.node().getComputedTextLength();
        dummyLabel.remove();

        return textLength;
    }

    private static yScale(
        height: number,
        maxYValue: number,
        minYValue: number
    ): ScaleLinear<number, number> {
        return scaleLinear().domain([minYValue, maxYValue]).range([height, 0]);
    }

    private static maxYTickLabelWidth(
        yAxisArea: Selection<SVGElement, unknown, BaseType, unknown>,
        maxYValue: number,
        yFormatter: (x: number) => string
    ) {
        const dummyLabel = yAxisArea
            .append("text")
            .text(transform(yFormatter, maxYValue));
        const textLength = dummyLabel.node().getComputedTextLength();
        dummyLabel.remove();

        return textLength;
    }

    /* Find the maximum Y axis value by comparing the max reduction with each data group */
    private static yValueLimits(
        data: ChartData,
        minOverwrite: number,
        maxOverwrite: number
    ): number[] {
        const [min, max] = Object.values(data).reduce(
            ([min, max], dataPointGroup) => {
                const dataPointValues = Object.values(dataPointGroup);
                return [
                    Math.min(...dataPointValues, min),
                    Math.max(...dataPointValues, max),
                ];
            },
            [Infinity, -Infinity]
        );

        if (
            minOverwrite != undefined &&
            minOverwrite < min &&
            maxOverwrite != undefined &&
            maxOverwrite > max
        ) {
            return [minOverwrite, maxOverwrite];
        }
        if (minOverwrite != undefined && minOverwrite < min) {
            return [minOverwrite, max];
        }
        if (maxOverwrite != undefined && maxOverwrite > max) {
            return [min, maxOverwrite];
        }

        return [min, max];
    }

    /* Grouped data used for d3 arrangement of the line charts */
    private static prepareData(
        data: ChartData,
        groupConfig: ChartDataGroupConfig<
            ChartGroupDataValues,
            LineChartDataGroupConfigItem
        >,
        valueMapper: (
            groupName: string,
            data: ChartData
        ) => (k: string) => { key: string | Date; value: number },
        selectedGroup: string
    ): LineChartData[] {
        const dataKeys = Object.keys(data);
        const dataGroups = LineChart.distinctDataGroups(data);
        const groupedData = dataGroups.map((groupKey) => ({
            name: groupKey,
            values: dataKeys.map(valueMapper(groupKey, data)),
            cssClass: getCssClassForKey(groupConfig, groupKey),
            ignore: selectedGroup && groupKey !== selectedGroup,
        }));
        return groupedData;
    }

    /** Find all distinct data groups/line keys by reducing to a Set and converting to an array */
    private static distinctDataGroups(data: ChartData): string[] {
        return [
            ...Object.values(data).reduce(
                (groups, current) =>
                    new Set<string>([...groups, ...Object.keys(current)]),
                new Set<string>()
            ),
        ];
    }

    private static xScaleWidth(
        parentWidth: number,
        margin: ChartMargin,
        yAxisTickWidth: number,
        yAxisLabelOffset: number
    ): number {
        return (
            parentWidth -
            (margin.left + margin.right + yAxisTickWidth + yAxisLabelOffset)
        );
    }

    render() {
        return (
            <Host>
                <div
                    id={this.chartId}
                    class={`uic-line-chart  ${this.lineChartRootClass}`}
                >
                    {/* Add the tooltip element outside of the svg as it's an HTML element */}
                    {!this.disableTooltip && (
                        <uic-tooltip
                            ref={(el) => (this.tooltip = el)}
                            class="uic-line-chart__tooltip"
                            placement="top-end"
                        >
                            <div></div>
                            <div
                                slot="tip"
                                class="uic-line-chart__tooltip-content"
                            ></div>
                        </uic-tooltip>
                    )}

                    {this.state == ChartState.LOADING && (
                        <div class="uic-line-chart__loading_indicator">
                            <uic-loader></uic-loader>
                        </div>
                    )}
                    {(this.state === ChartState.NODATA ||
                        (this.state !== ChartState.LOADING &&
                            (this.data == undefined ||
                                this.data == null ||
                                Object.keys(this.data).length === 0))) && (
                        <div class="uic-line-chart__no-data-indicator">
                            <div>{this.noDataText}</div>
                        </div>
                    )}
                </div>
            </Host>
        );
    }
}
